import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { SearchFilter } from 'ldt-people-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { LoginDialogService } from 'src/app/auth/service/login-dialog.service';
import { PeopleDetailsComponentQueryParams } from 'src/app/people/details/details.component';
import { QuickBuild, QuickBuildService } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { CompanyInfoService } from 'src/app/shared/company-search/company-info-service.service';
import { ShareThisViewService } from 'src/app/shared/share-this-view/share-this-view.service';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HeapUtils } from 'src/app/api-client/heap.utils';
import { WorkforceLayoutService } from '../workforce-layout.service';
import { WorkforceAppComponent } from '../app.wf.component';

@Component({
  selector: 'app-workforce-people',
  templateUrl: './people.component.html',
  styleUrls: ['./people.component.scss'],
  standalone: false,
})
export class WorkforcePeopleComponent implements OnInit, OnDestroy {
  isSmallScreen$: Observable<boolean>;
  mode: 'search' | 'explore' | 'bulk' | 'find' | 'find-companies' = 'explore';
  isAdmin: Observable<boolean>;
  palModeClickHandler: ((event: Event) => void) | null = null;
  isPalMode: boolean = false;
  quickBuild: QuickBuild | undefined;

  appliedFilter: PeopleDetailsComponentQueryParams | undefined;
  private routerSubscription: Subscription;
  constructor(
    public auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private peopleReportService: PeopleReportService,
    private shareThisViewService: ShareThisViewService,
    private loginDialogService: LoginDialogService,
    private notify: NotificationService,
    private companyInfoService: CompanyInfoService,
    private layoutService: WorkforceLayoutService,
    private parent: WorkforceAppComponent
  ) {
    this.isSmallScreen$ = this.layoutService.isSmallScreen$;
  }

  ngOnInit() {
    this.isAdmin = this.auth.$isAdmin;
    this.route.data.subscribe((data) => {
      if (data.mode) {
        this.mode = data.mode;
      }
    });

    this.auth.$palMode.subscribe((palMode) => {
      this.isPalMode = palMode;
      if (palMode) {
        this.addClickInterception();
      } else {
        this.removeClickInterception();
      }
    });

    // Check navigation state FIRST, before child components initialize
    // This is critical because child components (like FiltersComponent) may clear the navigation state
    this.checkNavigationState();

    // Listen for navigation events
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkNavigationState();
      });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private checkNavigationState() {
    const navigationState = history.state as {
      fromChat?: boolean;
      threadId?: string;
      quickBuild?: any;
    };
  }

  setMode(newMode: 'search' | 'explore' | 'bulk' | 'find' | 'find-companies') {
    const previousMode = this.mode;
    this.mode = newMode;

    // Track mode toggle in Heap
    HeapUtils.track('workforce_people_mode_toggle', {
      previous_mode: previousMode,
      new_mode: newMode,
      page: 'people',
    });

    this.router.navigateByUrl('people/' + this.mode);
  }

  toggleThinSidenav() {
    this.parent.toggleThinSidenav();
  }

  onShareClick() {
    const state: any = {};
    if (this.mode === 'search') {
      if (!this.quickBuild) {
        this.notify.error('Please enter some filters and click "Search" first.');
        return;
      }
      state.quickBuild = this.quickBuild;

      // Find any referenced companies and save them in the state for cache loading later
      const companySearchFilters = QuickBuildService.getFiltersFromQuickBuild(
        this.quickBuild!,
        'jobs_search'
      );
      const companiesToCache = companySearchFilters
        .map((filter) => (filter.filter as SearchFilter).string_values)
        .flat()
        .filter((company) => company !== undefined)
        .map((company) => company!.replace('-group', ''));
      const companyCache = this.companyInfoService.getCache();
      const companiesToSeed = Array.from(companyCache.values()).filter((company) =>
        companiesToCache.includes(company.id)
      );
      state.companyCache = companiesToSeed;
    }
    if (this.mode === 'explore') {
      state.filter = this.appliedFilter;
    }

    // Add the report cache for the current page to the state
    const config = {
      ...state,
      dataCache: this.peopleReportService.getContextCache(),
    };
    this.shareThisViewService.showShareDialog(
      this.auth.getSelectedOrgIdValue,
      '/people/' + this.mode,
      config
    );
  }

  onAppliedFilter(filter: PeopleDetailsComponentQueryParams | undefined) {
    this.appliedFilter = filter;
  }

  onQuickBuildChange(quickBuild: QuickBuild) {
    this.quickBuild = quickBuild;
  }

  private addClickInterception() {
    this.removeClickInterception();

    this.palModeClickHandler = (event: Event) => {
      // Check if the click target is inside allowed elements
      const target = event.target as Element;
      const isAllowedTarget =
        // Login dialog elements
        target.closest('.login-dialog-container') ||
        target.closest('.mat-mdc-dialog-container') ||
        target.closest('.mat-dialog-container') ||
        target.closest('.login-dialog-backdrop') ||
        target.closest('#login-signup') ||
        target.closest('.login-signup-main') ||
        // Filters component show/edit filters toggle buttons
        target.closest('#filters-toggle-container') ||
        // AG Grid in filters component people tab
        target.closest('#people-grid-ag-grid') ||
        target.closest('.ag-grid-angular') ||
        target.closest('.ag-row') ||
        target.closest('.ag-cell');

      if (!isAllowedTarget) {
        this.loginDialogService.openLoginDialog();
        // HeapUtils.track('public_share_view_signup_dialog_shown', {});
        event.stopPropagation();
        event.preventDefault();
      }
    };

    document.addEventListener('click', this.palModeClickHandler, true);
  }

  private removeClickInterception() {
    if (this.palModeClickHandler) {
      document.removeEventListener('click', this.palModeClickHandler, true);
      this.palModeClickHandler = null;
    }
  }
}
